/**
 * Avatar API helpers
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

/**
 * Get presigned URLs for avatar upload
 * @param {string} filename
 * @param {string} contentType
 * @param {string} token - Auth token
 * @returns {Promise<Object>}
 */
export async function presignAvatar(filename, contentType, token) {
  const response = await fetch(`${API}/media/avatar/presign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ filename, contentType })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get presigned URL');
  }

  return response.json();
}

/**
 * Upload file to local storage
 * @param {File} file
 * @param {string} key
 * @param {string} token
 * @returns {Promise<Object>}
 */
export async function uploadLocal(file, key, token) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('key', key);

  const response = await fetch(`${API}/media/upload-local`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    body: formData
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to upload file');
  }

  return response.json();
}

/**
 * Attach avatar to user profile
 * @param {Object} avatarData - {key, url, thumbUrl, width, height}
 * @param {string} token
 * @returns {Promise<Object>}
 */
export async function attachAvatar(avatarData, token) {
  const response = await fetch(`${API}/users/me/avatar/attach`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(avatarData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to attach avatar');
  }

  return response.json();
}

/**
 * Remove user avatar
 * @param {string} token
 * @returns {Promise<Object>}
 */
export async function removeAvatar(token) {
  const response = await fetch(`${API}/users/me/avatar`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to remove avatar');
  }

  return response.json();
}
